import React from "react";

/** Base shimmer block. */
export const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse rounded-md bg-slate-200/80 ${className}`} />
);

const SkeletonCard = ({ lines = 2 }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <Skeleton className="h-8 w-8 rounded-lg" />
    <Skeleton className="mt-4 h-3 w-2/3" />
    <Skeleton className="mt-2 h-6 w-1/2" />
    {lines > 2 && <Skeleton className="mt-2 h-3 w-1/3" />}
  </div>
);

const SkeletonPanel = ({ rows = 4 }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <Skeleton className="h-4 w-1/3" />
    <div className="mt-4 space-y-3">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex items-center justify-between gap-3">
          <Skeleton className="h-9 w-9 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
          <Skeleton className="h-6 w-20" />
        </div>
      ))}
    </div>
  </div>
);

/** Skeleton for the main admin dashboard. */
export const DashboardSkeleton = () => (
  <div className="space-y-4" aria-busy="true" aria-label="Loading dashboard">
    <div className="flex justify-end">
      <Skeleton className="h-9 w-24" />
    </div>

    {/* Stat cards */}
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 8 }).map((_, index) => (
        <SkeletonCard key={index} />
      ))}
    </div>

    {/* Exam overview */}
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-9 w-40" />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="mt-2 h-6 w-1/2" />
          </div>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <SkeletonPanel key={index} rows={3} />
        ))}
      </div>
    </div>

    {/* Recent activity */}
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <SkeletonPanel key={index} rows={4} />
      ))}
    </div>

    {/* Recent purchases */}
    <SkeletonPanel rows={4} />
  </div>
);

/** Generic skeleton used by admin pages while their endpoint is loading. */
export const AdminPageSkeleton = () => (
  <div className="space-y-4" aria-busy="true" aria-label="Loading">
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <Skeleton className="h-5 w-1/3" />
      <Skeleton className="mt-3 h-3 w-2/3" />
    </div>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <SkeletonCard key={index} lines={3} />
      ))}
    </div>
    <SkeletonPanel rows={6} />
  </div>
);

export default AdminPageSkeleton;
